<script setup lang="ts">
import PdsIcon from '@/components/pds/PdsIcon.vue';
import { computed, ref } from 'vue';

// Dates en « AAAA-MM-JJ », comparables telles quelles : l'ordre des chaînes est celui des jours.
// `to` fait du calendrier un sélecteur de période : `selected` en est le premier jour, `to` le dernier, et les jours
// entre les deux se teintent. `today` repère aujourd'hui quand `min` n'est pas lui (une période peut être passée).
const props = defineProps<{
  selected: string;
  min: string;
  max: string;
  to?: string;
  today?: string;
}>();
const emit = defineEmits<{ pick: [iso: string] }>();

const WEEKDAYS = ['lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.', 'dim.'];

const toIso = (date: Date) =>
  [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
const fromIso = (value: string) => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};
const firstOfMonth = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), 1);

// Le mois montré à l'ouverture est celui du jour déjà choisi.
const month = ref(firstOfMonth(fromIso(props.selected || props.min)));

const monthLabel = computed(() => {
  const label = new Intl.DateTimeFormat('fr-BE', {
    month: 'long',
    year: 'numeric',
  }).format(month.value);
  return label.charAt(0).toUpperCase() + label.slice(1);
});

// Semaine commençant le lundi : les cases vides d'avant le 1er complètent la première ligne.
const cells = computed(() => {
  const year = month.value.getFullYear();
  const index = month.value.getMonth();
  const blanks = (month.value.getDay() + 6) % 7;
  const length = new Date(year, index + 1, 0).getDate();
  return [
    ...Array<null>(blanks).fill(null),
    ...Array.from({ length }, (_, i) => toIso(new Date(year, index, i + 1))),
  ];
});

const canGoBack = computed(
  () =>
    toIso(new Date(month.value.getFullYear(), month.value.getMonth(), 0)) >=
    props.min,
);
const canGoForward = computed(
  () =>
    toIso(new Date(month.value.getFullYear(), month.value.getMonth() + 1, 1)) <=
    props.max,
);

const shift = (months: number) => {
  month.value = new Date(
    month.value.getFullYear(),
    month.value.getMonth() + months,
    1,
  );
};

const arrow =
  'bg-second text-encre flex size-10 cursor-pointer items-center justify-center rounded-full disabled:cursor-not-allowed disabled:opacity-40';
</script>

<template>
  <div
    role="group"
    aria-label="Calendrier"
    class="bg-creux flex flex-col gap-3 rounded-[24px] p-4"
    data-testid="pickup-calendar"
  >
    <div class="flex items-center justify-between gap-3">
      <button
        type="button"
        :class="arrow"
        aria-label="Mois précédent"
        :disabled="!canGoBack"
        @click="shift(-1)"
      >
        <PdsIcon name="chevron-left" :size="20" />
      </button>
      <p class="text-[16px] leading-6 font-semibold" aria-live="polite">
        {{ monthLabel }}
      </p>
      <button
        type="button"
        :class="arrow"
        aria-label="Mois suivant"
        :disabled="!canGoForward"
        @click="shift(1)"
      >
        <PdsIcon name="chevron-right" :size="20" />
      </button>
    </div>

    <div class="grid grid-cols-7 gap-1 text-center">
      <span
        v-for="weekday in WEEKDAYS"
        :key="weekday"
        class="text-encre-douce text-[12px] leading-[17px] font-semibold"
      >
        {{ weekday }}
      </span>
      <template v-for="(cell, index) in cells" :key="cell ?? `blank-${index}`">
        <span v-if="!cell" />
        <button
          v-else
          type="button"
          :aria-pressed="cell === selected || cell === to"
          :aria-current="cell === (today ?? min) ? 'date' : undefined"
          :disabled="cell < min || cell > max"
          class="mx-auto flex size-10 cursor-pointer items-center justify-center rounded-full text-[14px] leading-5 font-semibold disabled:cursor-not-allowed disabled:opacity-30"
          :class="
            cell === selected || cell === to
              ? 'bg-encre text-carte'
              : to && cell > selected && cell < to
                ? 'bg-second text-encre'
                : cell === (today ?? min)
                  ? 'text-encre ring-encre ring-1 ring-inset'
                  : 'text-encre'
          "
          @click="emit('pick', cell)"
        >
          {{ Number(cell.slice(8)) }}
        </button>
      </template>
    </div>
  </div>
</template>
